import { cn, Icon } from '@lqa-frontend/ui-system';
import * as React from 'react';
import { BodyM } from '../typography';
import type { IndexedStep, IStep, StepState } from '.';
import { resolveState, StepCircleContent } from '.';

const StepCircleClassesMap: Record<
	StepState,
	{ active: string; notActive: string }
> = {
	normal: {
		active: 'bg-text-link text-white border-outline-text-link',
		notActive: 'bg-transparent text-text-link border-text-link',
	},
	completed: {
		active: 'bg-text-link text-white border-outline-text-link',
		notActive: 'bg-transparent text-text-link border-text-link',
	},
	error: {
		active: 'bg-danger text-white border-outline-danger',
		notActive: 'bg-transparent text-danger border-danger',
	},
	disabled: {
		active: 'bg-outline-neutral text-white border-outline-neutral',
		notActive: 'bg-outline-neutral text-white border-outline-neutral',
	},
};

const StepTitleClassesMap: Record<StepState, string> = {
	normal: 'text-text-link',
	completed: 'text-text-primary',
	error: 'text-danger',
	disabled: 'text-text-tertiary',
};

export interface IStepperProps {
	currentStep: number;
	steps: IStep[];
	className?: string;
	onStepChange?: (step: number) => void;
}

function Stepper({
	currentStep,
	steps,
	className,
	onStepChange,
}: IStepperProps) {
	const handleStepClick = (step: IndexedStep) => {
		if (!onStepChange || step.state === 'disabled') return;
		onStepChange(step.index);
	};

	return (
		<nav
			aria-label="Progress"
			className={cn('w-full overflow-x-auto', className)}
		>
			<ol className="flex items-center gap-16 w-fit mx-auto">
				{steps.map((step, i) => {
					const indexedStep: IndexedStep = {
						...step,
						index: i + 1,
						isActive: i + 1 === currentStep,
						state: resolveState(step, currentStep, i + 1),
					};
					const isClickable = onStepChange && indexedStep.state !== 'disabled';

					return (
						<li key={indexedStep.index} className="flex items-center gap-16">
							<div
								className={cn(
									'flex items-center gap-8 flex-shrink-0',
									isClickable
										? 'cursor-pointer hover:opacity-80 transition-opacity'
										: 'cursor-not-allowed'
								)}
								onClick={() => handleStepClick(indexedStep)}
								role={isClickable ? 'button' : undefined}
								tabIndex={isClickable ? 0 : undefined}
								onKeyDown={(e) => {
									if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
										e.preventDefault();
										handleStepClick(indexedStep);
									}
								}}
							>
								<span
									className={cn(
										'flex size-40 items-center justify-center rounded-max border-2 transition-all',
										StepCircleClassesMap[indexedStep.state][
											indexedStep.isActive ? 'active' : 'notActive'
										]
									)}
									aria-current={
										currentStep === indexedStep.index ? 'step' : undefined
									}
								>
									<StepCircleContent step={indexedStep} />
								</span>

								<BodyM
									className={cn(
										'whitespace-nowrap',
										StepTitleClassesMap[indexedStep.state]
									)}
								>
									{indexedStep.title}
								</BodyM>

								{i < steps.length - 1 && (
									<Icon name="chevronRight" className="text-neutral" />
								)}
							</div>
						</li>
					);
				})}
			</ol>
		</nav>
	);
}

export { Stepper };
